import {
  alpha,
  Box,
  ButtonBase,
  FormHelperText,
  InputAdornment,
  MenuItem,
  Switch,
  SxProps,
  TextField,
  Theme,
  ToggleButton,
  ToggleButtonGroup,
  Typography
} from "@mui/material"
import CheckCircleRounded from "@mui/icons-material/CheckCircleRounded"
import RadioButtonUncheckedRounded from "@mui/icons-material/RadioButtonUncheckedRounded"

import { SettingSpec } from "../connectorFeatures"

type FieldProps = {
  spec: SettingSpec
  value: string
  onChange: (value: string) => void
  disabled?: boolean
  placeholder?: string
  sx?: SxProps<Theme>
}

const BOOLEAN_VALUES = new Set(["true", "false"])
const MAX_UNIT_LENGTH = 8

const isBooleanSelect = (spec: SettingSpec) =>
  spec.inputType === "select" &&
  (spec.options ?? []).length === 2 &&
  (spec.options ?? []).every((o) => BOOLEAN_VALUES.has(o.value))

/** "Price markup (%)" -> label "Price markup", unit "%"; long parentheticals stay in the label. */
const splitUnit = (label: string): { label: string; unit: string | null } => {
  const match = /^(.*\S)\s*\(([^)]+)\)$/.exec(label)
  if (!match || match[2].length > MAX_UNIT_LENGTH) return { label, unit: null }
  return { label: match[1], unit: match[2] }
}

const FieldLabel = ({ spec, label }: { spec: SettingSpec; label?: string }) => (
  <Typography variant="body2" fontWeight={600} color="text.primary">
    {label ?? spec.label}
    {spec.required && (
      <Typography component="span" color="error.main" ml={0.25}>
        *
      </Typography>
    )}
  </Typography>
)

const Help = ({ text }: { text?: string | null }) =>
  text ? (
    <FormHelperText sx={{ mx: 0, mt: 0.75, fontSize: "0.8rem", lineHeight: 1.5, color: "text.secondary" }}>
      {text}
    </FormHelperText>
  ) : null

/** Help text that is only an example ("e.g. my-store.myshopify.com") reads better as the placeholder. */
const exampleFrom = (helpText?: string | null): string | null => {
  const match = /^e\.g\.\s+(\S.*)$/i.exec(helpText?.trim() ?? "")
  return match ? match[1] : null
}

/** Two or three choices shown side by side as cards, so each is visible before choosing. */
const ChoiceCards = ({ spec, value, onChange, disabled, sx }: FieldProps) => {
  const options = spec.options ?? []
  return (
    <Box sx={sx}>
      <FieldLabel spec={spec} />
      <Box
        role="radiogroup"
        aria-label={spec.label}
        sx={{
          mt: 1,
          display: "grid",
          gap: 1.25,
          gridTemplateColumns: { xs: "1fr", sm: `repeat(${options.length}, 1fr)` }
        }}
      >
        {options.map((option) => {
          const selected = option.value === value
          return (
            <ButtonBase
              key={option.value}
              role="radio"
              aria-checked={selected}
              disabled={disabled}
              onClick={() => onChange(option.value)}
              sx={(theme) => ({
                justifyContent: "flex-start",
                gap: 1.25,
                px: 1.75,
                py: 1.5,
                borderRadius: 2,
                textAlign: "left",
                border: `1.5px solid ${selected ? theme.palette.primary.main : theme.palette.divider}`,
                bgcolor: selected ? alpha(theme.palette.primary.main, 0.06) : "transparent",
                transition: "border-color 0.15s ease, background-color 0.15s ease",
                "&:hover": { borderColor: selected ? theme.palette.primary.main : theme.palette.text.secondary }
              })}
            >
              {selected ? (
                <CheckCircleRounded fontSize="small" color="primary" />
              ) : (
                <RadioButtonUncheckedRounded fontSize="small" sx={{ color: "text.disabled" }} />
              )}
              <Typography variant="body2" fontWeight={selected ? 600 : 500}>
                {option.label}
              </Typography>
            </ButtonBase>
          )
        })}
      </Box>
      <Help text={spec.helpText} />
    </Box>
  )
}

/** A yes/no setting as a switch row rather than a two-item dropdown. */
const SwitchRow = ({ spec, value, onChange, disabled, sx }: FieldProps) => (
  <Box sx={sx}>
    <Box display="flex" alignItems="flex-start" justifyContent="space-between" gap={2}>
      <Box>
        <FieldLabel spec={spec} />
        <Help text={spec.helpText} />
      </Box>
      <Switch
        checked={value === "true"}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked ? "true" : "false")}
        inputProps={{ "aria-label": spec.label }}
      />
    </Box>
  </Box>
)

const Segmented = ({ spec, value, onChange, disabled, sx }: FieldProps) => (
  <Box sx={sx}>
    <FieldLabel spec={spec} />
    <ToggleButtonGroup
      exclusive
      size="small"
      value={value}
      disabled={disabled}
      onChange={(_, next: string | null) => next !== null && onChange(next)}
      sx={{ mt: 1, display: "flex", "& .MuiToggleButton-root": { flex: 1, textTransform: "none", py: 0.75 } }}
    >
      {(spec.options ?? []).map((option) => (
        <ToggleButton key={option.value} value={option.value}>
          {option.label}
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
    <Help text={spec.helpText} />
  </Box>
)

/**
 * One connector setting, drawn from its spec: yes/no as a switch, a short
 * choice as cards (`variant="cards"`) or a segmented control, a long one as
 * a dropdown, and a unit like "(%)" moved into the field as a suffix.
 */
const SettingField = (props: FieldProps & { variant?: "cards" | "compact" }) => {
  const { spec, value, onChange, disabled, placeholder, sx, variant = "compact" } = props
  const options = spec.options ?? []

  if (isBooleanSelect(spec)) return <SwitchRow {...props} />
  if (spec.inputType === "select" && options.length <= 3)
    return variant === "cards" ? <ChoiceCards {...props} /> : <Segmented {...props} />

  const { label, unit } = splitUnit(spec.label)
  const example = exampleFrom(spec.helpText)

  if (spec.inputType === "select")
    return (
      <Box sx={sx}>
        <FieldLabel spec={spec} />
        <TextField
          select
          fullWidth
          size="small"
          sx={{ mt: 1 }}
          disabled={disabled}
          value={value}
          onChange={(e) => onChange(e.target.value)}
        >
          {options.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
        <Help text={spec.helpText} />
      </Box>
    )

  return (
    <Box sx={sx}>
      <FieldLabel spec={spec} label={label} />
      <TextField
        fullWidth
        size="small"
        sx={{ mt: 1 }}
        disabled={disabled}
        value={value}
        placeholder={placeholder ?? example ?? undefined}
        type={spec.inputType === "number" ? "number" : spec.secret ? "password" : "text"}
        autoComplete={spec.secret ? "new-password" : "off"}
        inputProps={{ "aria-label": spec.label, ...(spec.inputType === "number" ? { min: 0 } : {}) }}
        InputProps={unit ? { endAdornment: <InputAdornment position="end">{unit}</InputAdornment> } : undefined}
        onChange={(e) => onChange(e.target.value)}
      />
      {!example && <Help text={spec.helpText} />}
    </Box>
  )
}

export default SettingField
